import { Close, MoreVert } from "@mui/icons-material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import GridOnIcon from "@mui/icons-material/GridOn";
import TimelapseIcon from "@mui/icons-material/Timelapse";
import {
  Box,
  Container,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import { FeatureCollection } from "@turf/turf";
import { useNotification } from "app/NotificationProvider";
import { mixpanel } from "common/analytics";
import { PageTitleBar } from "common/components";
import { DryMatterIcon, GrassIcon, HectaresIcon } from "common/icons";
import { useTranslation } from "common/locales";
import { DarkGreenButton } from "features/DarkGreenButton";
import { FieldEditActionMenu } from "features/fieldDetail";
import LivestockSection from "features/fieldDetail/LivestockSection";
import AfterFieldCreatedDialog from "features/fieldEdit/AfterFieldCreatedDialog";
import GrassTypeDropdown from "features/fieldEdit/GrassTypeDropdown";
import { FieldEventList } from "features/fieldEventList";
import CoverEntryThumbsDownDialog from "features/singleCoverInput/CoverEntryThumbsDownDialog";
import CoverEntryThumbsUpDialog from "features/singleCoverInput/CoverEntryThumbsUpDialog";
import { moveAnimalsIn } from "features/singleCoverInput/UpdateBiomassAfterMoveInDialog";
import {
  changeFieldVisibility,
  Field,
  FIELD_TYPE,
  getAllFieldsForSilage,
  getArableFields,
  getDemand,
  getFieldWithAnimalsForAnimalGroupId,
  getGrainFields,
  getPastureFields,
  GRASS_TYPE,
  openMoveOutEventDialog,
  selectAllAnimalGroups,
  selectAnimalGroupById,
  selectCurrentFarm,
  setIsEditing,
  setZoomToGeom,
  updateField,
  useAppDispatch,
  useAppSelector,
} from "model";
import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";

interface Props {
  field: Field;
}

interface LocationState {
  previousPath?: string;
  previousSearch?: string;
}

export default function FieldDetails({ field }: Props) {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();
  const location = useLocation<LocationState>();
  const currentFarm = useAppSelector(selectCurrentFarm);
  const iconSize = "medium";

  const animalGroup = useAppSelector((state) =>
    selectAnimalGroupById(state, field.animalGroup ?? "")
  );

  const demand = useAppSelector((state) =>
    getDemand(state, animalGroup?.guid ?? "")
  );
  const animalGroups = useAppSelector(selectAllAnimalGroups);

  // Get field lists for navigation logic
  const grainFields = useAppSelector(getGrainFields);
  const arableFields = useAppSelector(getArableFields);
  const silageFields = useAppSelector(getAllFieldsForSilage);
  const pastureFields = useAppSelector(getPastureFields);

  const dispatch = useAppDispatch();
  const { notify } = useNotification();

  const fieldGeom = {
    type: "FeatureCollection",
    features: [field.geom],
  } as FeatureCollection;

  const overgrazed = Boolean(
    animalGroup?.postGrazingTarget &&
      field.dryMatterNow &&
      field.dryMatterNow < animalGroup.postGrazingTarget
  );

  let availableDryMatter;
  if (animalGroup && animalGroup.postGrazingTarget) {
    availableDryMatter = Math.floor(
      ((field.dryMatterNow - animalGroup.postGrazingTarget) * field.area) /
        10000
    );
    if (availableDryMatter < 0) {
      availableDryMatter = 0;
    }
  }

  let grazingDaysAhead;
  if (availableDryMatter && demand) {
    grazingDaysAhead = availableDryMatter / demand;
  }

  const currentFieldWithAnimals = useAppSelector(
    getFieldWithAnimalsForAnimalGroupId(field.animalGroup)
  );

  const canMoveIn =
    field.animalGroup && field.guid !== currentFieldWithAnimals?.guid;

  useEffect(() => {
    dispatch(setZoomToGeom(fieldGeom));
  }, [field.guid, dispatch]);

  const handleBack = () => {
    if (location.state?.previousPath) {
      history.push({
        pathname: location.state.previousPath,
        search: location.state.previousSearch || "",
      });
      return;
    }

    // If field belongs to an animal group, navigate to that animal group
    if (field.animalGroup) {
      history.push(`/animal-group/${field.animalGroup}`);
      return;
    }

    // Otherwise, determine field type and navigate to appropriate fields list
    const isGrainField = grainFields.some((f) => f?.guid === field.guid);
    if (isGrainField) {
      history.push("/fields-list/grain");
      return;
    }

    const isArableField = arableFields.some((f) => f?.guid === field.guid);
    if (isArableField) {
      history.push("/fields-list/arable");
      return;
    }

    const isSilageField = silageFields.some((f) => f?.guid === field.guid);
    if (isSilageField) {
      history.push("/fields-list/silage");
      return;
    }

    const isPastureField = pastureFields.some((f) => f?.guid === field.guid);
    if (isPastureField) {
      history.push("/fields-list/pasture");
      return;
    }

    // Fallback to farm
    backToFarm();
  };

  const backToFarm = () => {
    if (currentFarm?.guid) {
      history.push(`/farm/${currentFarm.guid}`);
    }
  };

  async function onMoveIn() {
    try {
      if (field) {
        if (currentFieldWithAnimals) {
          dispatch(
            openMoveOutEventDialog({
              open: true,
              newFieldWithAnimals: field,
              currentFieldWithAnimals: currentFieldWithAnimals,
            })
          );
        } else {
          await moveAnimalsIn(
            field,
            currentFieldWithAnimals,
            new Date(),
            dispatch,
            notify,
            t
          );
        }
      }
    } catch (e) {
      console.log(e);
    }
  }

  async function handleGrassTypeChange(
    value: GRASS_TYPE | undefined,
    field: Field
  ) {
    if (value && field) {
      try {
        console.log(value);
        console.log(field);
        //field.grassType = value;
        //console.log("after assign");
        await dispatch(
          updateField({
            ...field,
            grassType: value, // Ensure grassType is updated
          })
        ).then(unwrapResult);
        console.log("after dispatch");
        mixpanel.track("Field grass type updated", {
          fieldGuid: field.guid,
          grassType: value,
        });
      } catch (e) {
        console.error("Failed to update grass type", e);
        notify(t("fieldinput.grassType.updateFailed"), "error");
      }
    }
  }
  return (
    <>
      <PageTitleBar title={"Field"} onBack={handleBack} showDivider />

      {/* New Design - Main Content Card */}
      <Box sx={{ pb: 2, backgroundColor: "#F7FCF1" }}>
        <Box
          sx={{
            backgroundColor: "white",
            px: 3,
          }}
        >
          {/* Header Section */}
          <Box sx={{ pt: 2, pb: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 2,
              }}
            >
              <Box>
                <Typography
                  variant="h5"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    color: "text.primary",
                    lineHeight: 1.2,
                    mb: 0.5,
                  }}
                >
                  {field.name}
                </Typography>
                {overgrazed && (
                  <Typography
                    color="error"
                    variant="body2"
                    sx={{ fontWeight: 500 }}
                  >
                    {t("fieldview.overgrazed")}
                  </Typography>
                )}
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: "flex", gap: 1 }}>
                <IconButton onClick={openMenu}>
                  <MoreVert />
                </IconButton>
                {isMobile && (
                  <IconButton
                    size="small"
                    onClick={handleBack}
                    sx={{
                      backgroundColor: "#DBF4C2",
                      color: "white",
                      width: 32,
                      height: 32,
                    }}
                  >
                    <Close fontSize="small" htmlColor="#1C4321" />
                  </IconButton>
                )}
              </Box>
            </Box>

            {/* Move Animals Button */}
            <MoveAnimalsButton
              canMoveIn={!!canMoveIn}
              onMoveIn={onMoveIn}
              fieldHasAnimalGroup={!!field?.animalGroup}
            />
          </Box>

          {/* Field Metrics Section */}
          <Box sx={{ pb: 3 }}>
            <Typography
              variant="h6"
              component="h2"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: "text.primary",
                paddingTop: 4,
              }}
            >
              {t("fieldDetail.title")}
            </Typography>

            <Stack spacing={2}>
              {/* Available Dry Matter */}
              {animalGroup && animalGroup.postGrazingTarget && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <DryMatterIcon fontSize={iconSize} />
                    </Box>
                    <Box>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, color: "black" }}
                      >
                        {field && !field.invalidatedTimestamp
                          ? `${availableDryMatter || 0} kgDM`
                          : t("fieldview.noDryMatterNow.text")}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "black", fontWeight: 400 }}
                      >
                        {t("fieldDetail.availableDryMatter")}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Divider after Available Dry Matter */}
              {animalGroup && animalGroup.postGrazingTarget && <Divider />}

              {/* Grazing Days Ahead */}
              {animalGroup && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <TimelapseIcon fontSize={iconSize} />
                    </Box>
                    <Box>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, color: "black" }}
                      >
                        {field && !field.invalidatedTimestamp
                          ? `${
                              grazingDaysAhead
                                ? Math.floor(grazingDaysAhead)
                                : 0
                            }d ${
                              grazingDaysAhead
                                ? Math.round((grazingDaysAhead % 1) * 24)
                                : 0
                            }h`
                          : t("fieldview.noDryMatterNow.text")}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "black", fontWeight: 400 }}
                      >
                        {t("fieldDetail.grazingDaysAhead")}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Divider after Grazing Days Ahead */}
              {animalGroup && <Divider />}

              {/* Total Cover */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <GridOnIcon fontSize={iconSize} />
                  </Box>
                  <Box>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 500, color: "black" }}
                    >
                      {field && !field.invalidatedTimestamp
                        ? `${field.dryMatterNow} kgDM/ha`
                        : t("fieldview.noDryMatterNow.text")}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "black", fontWeight: 400 }}
                    >
                      {t("fieldDetail.totalCover")}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <CoverEntryThumbsUpDialog field={field} />
                  <CoverEntryThumbsDownDialog field={field} />
                </Box>
              </Box>

              {/* Divider after Total Cover */}
              <Divider />

              {/* Area */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <HectaresIcon fontSize={iconSize} />
                  </Box>
                  <Box>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 500, color: "black" }}
                    >
                      {field ? `${(field.area / 10000).toFixed(2)} ha` : ""}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "black", fontWeight: 400 }}
                    >
                      {t("fieldDetail.area")}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  sx={{
                    color: "text.secondary",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                  onClick={() => {
                    dispatch(setIsEditing());
                    dispatch(
                      changeFieldVisibility({
                        guid: field.guid,
                        visibility: "none",
                      })
                    );
                    mixpanel.track("FieldEdit successful");
                    history.push(`/edit-field/${field.guid}/enter-details`);
                  }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Box>

              {/* Divider after Area */}
              <Divider />

              {/* Grass Type */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <GrassIcon fontSize={iconSize} />
                  </Box>
                  <Box>
                    {!field.grassType ? (
                      <GrassTypeDropdown
                        disabled={false}
                        onChange={async (value?: GRASS_TYPE) => {
                          await handleGrassTypeChange(value, field);
                        }}
                        currentGrassType={field.grassType}
                        grassTypeInputError={false}
                        label={"fieldinput.grassType.text"}
                      />
                    ) : (
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, color: "black" }}
                      >
                        {t(`fieldinput.grassType.option.${field.grassType}`)}
                      </Typography>
                    )}
                    <Typography
                      variant="body2"
                      sx={{ color: "black", fontWeight: 400 }}
                    >
                      {t("fieldDetail.grassType")}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  sx={{
                    color: "text.secondary",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                  onClick={() => {
                    dispatch(setIsEditing());
                    dispatch(
                      changeFieldVisibility({
                        guid: field.guid,
                        visibility: "none",
                      })
                    );
                    mixpanel.track("FieldEdit successful");
                    history.push(`/edit-field/${field.guid}/enter-details`);
                  }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Box>
            </Stack>
          </Box>
        </Box>

        {/* Field Edit Action Menu */}
        <FieldEditActionMenu
          anchor={menuAnchor}
          close={closeMenu}
          fieldId={field.guid || ""}
          fieldType={FIELD_TYPE.GRASS}
        />

        <Box
          sx={{
            backgroundColor: "white",
            mt: 2,
          }}
        >
          {/* Rest of the existing content */}
          <LivestockSection field={field} onGroupChange={moveToAnimalGroup} />
        </Box>

        <Box
          sx={{
            backgroundColor: "white",
            mt: 2,
          }}
        >
          <FieldEventListContainer>
            <FieldEventList field={field} />
          </FieldEventListContainer>
        </Box>
      </Box>

      <AfterFieldCreatedDialog />
    </>
  );

  function openMenu(event: React.MouseEvent<HTMLButtonElement>) {
    setMenuAnchor(event.currentTarget);
  }

  function closeMenu() {
    setMenuAnchor(null);
  }
  async function moveToAnimalGroup(guid?: string) {
    const animalGroupName = animalGroups.find((ag) => ag.guid === guid)?.name;
    try {
      await dispatch(updateField({ ...field, animalGroup: guid })).then(
        unwrapResult
      );
      mixpanel.track("FieldAssign successful");
      notify(
        guid
          ? t("fieldlistmenu.notification.movedAnimalGroup", {
              fieldName: field?.name,
              animalGroupName: animalGroupName,
            })
          : t("fieldlistmenu.notification.movedToSilage", {
              fieldName: field?.name,
            })
      );
    } catch (e) {
      mixpanel.track("FieldAssign failed");
      notify(t("fieldlistmenu.notification.moveFailed"), "error");
    }
  }
}

// Removed unused styled components - now using PageTitleBar
/* const Header = styled.div`
  background-color: white;
  padding: 16px;
  padding-top: calc(env(safe-area-inset-top) + 16px);
  display: flex;
  align-items: center;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.1);
  position: relative;
`;

const BackButton = styled(IconButton)`
  position: absolute;
  left: 8px;
`;

const TitleContainer = styled.div`
  flex: 1;
  text-align: center;
  margin: 0 48px;
`; */

const FieldEventListContainer = styled(Container)`
  flex-grow: 1;
`;

const TooltipChild = styled.div(
  ({ theme }) => `
  width: 100%;
`
);

interface MoveAnimalsButtonProps {
  canMoveIn: boolean;
  onMoveIn: () => Promise<void>;
  fieldHasAnimalGroup: boolean;
}

function MoveAnimalsButton({
  canMoveIn,
  onMoveIn,
  fieldHasAnimalGroup,
}: MoveAnimalsButtonProps) {
  const { t } = useTranslation();
  const [isSendingMoveRequest, setSendingMoveRequest] = useState(false);

  const disabled = isSendingMoveRequest || !canMoveIn;

  const handleButtonClick = async () => {
    if (disabled) return;
    setSendingMoveRequest(true);
    await onMoveIn();
    setSendingMoveRequest(false);
  };

  const MoveButton = (
    <Box
      sx={{
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      <DarkGreenButton
        title={t("fieldview.moveAnimals.text")}
        handleUploadClick={handleButtonClick}
        maxWidth="auto"
      />
    </Box>
  );

  return canMoveIn ? (
    MoveButton
  ) : (
    <Tooltip
      title={
        fieldHasAnimalGroup
          ? t("fieldlistmenu.moveIn.tooltip.sameField")
          : t("fieldlistmenu.moveIn.tooltip.silage")
      }
    >
      <TooltipChild>{MoveButton}</TooltipChild>
    </Tooltip>
  );
}
